import { useEffect, useRef, useState, type FormEvent } from "react";
import type { TenantAiSettings } from "@detect/shared";
import { useTenant } from "../../contexts/TenantContext";
import { tenantService } from "../../services/tenants";
import { LoadingSpinner, ErrorState } from "../../components/common";

/**
 * AI analysis (#153). OFF by default and opt-in per organization. When on, a change window can ask Detect to judge whether
 * a change achieved its intent: Detect sends the change's title, purpose and success text, and a short before/after summary of
 * the resources in that change's scope, to Gemini on Google Vertex AI in Detect's own cloud project. The answer is advisory
 * and never changes a change verdict. Only an account admin can switch it on or off.
 */
export function AiTab() {
  const { tenantId, hasRole } = useTenant();
  const canEdit = hasRole("account_admin");
  const [saved, setSaved] = useState<TenantAiSettings | null>(null);
  const [draft, setDraft] = useState<TenantAiSettings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  /** Bumped on every (re)load: a save that resolves after a tenant switch belongs to an older generation. */
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    setSaved(null);
    setDraft(null);
    setError(null);
    setSaving(false);
    if (!tenantId) return;
    let live = true;
    tenantService.current(tenantId).then(
      (t) => {
        if (!live) return;
        setSaved(t.ai);
        setDraft(t.ai);
      },
      (err: unknown) => live && setError(err),
    );
    return () => {
      live = false;
    };
  }, [tenantId]);

  if (error !== null && draft === null) return <ErrorState error={error} />;
  if (!draft || !saved) return <LoadingSpinner />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId || !canEdit) return;
    if (
      draft.changeIntentAnalysis &&
      !saved.changeIntentAnalysis &&
      !window.confirm(
        "Detect will send the title, purpose and success text of changes that use AI analysis, and a summary of their in-scope resources' metrics and alerts, to Google Vertex AI (Gemini) in Detect's cloud project. Turn it on?",
      )
    )
      return;
    const gen = generation.current;
    setSaving(true);
    setError(null);
    try {
      const t = await tenantService.updateAi(tenantId, draft);
      if (generation.current !== gen) return;
      setSaved(t.ai);
      setDraft(t.ai);
    } catch (err) {
      if (generation.current === gen) setError(err);
    } finally {
      if (generation.current === gen) setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {error !== null && <ErrorState error={error} />}
      <div className="form-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={draft.changeIntentAnalysis}
            onChange={(e) => setDraft({ ...draft, changeIntentAnalysis: e.target.checked })}
          />
          Allow AI analysis of change results
        </label>
        <p className="text-muted">
          Off by default. When on, a change window can be set to “Analysed by AI”: after the window, Detect asks Gemini on
          Google Vertex AI (in Detect&apos;s own cloud project) whether the change achieved what it set out to do.
        </p>
        <p className="text-muted">
          <strong>What is sent:</strong> the change&apos;s title, purpose and success text, and for the resources the change
          covers: a short summary of how their metrics and active alerts looked in the hour before the window and just after
          it. <strong>What is never sent:</strong> credentials, people&apos;s details, other organizations&apos; data, or raw
          logs.
        </p>
        <p className="text-muted">
          The answer is advisory: it is shown beside the change verdict and never changes it. A person can always record
          their own mark, which overrides it. If the AI is unavailable the result is “unclear”. Each call is recorded
          (tokens, time, outcome) for cost visibility.
        </p>
      </div>
      {canEdit ? (
        <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
          {saving ? "Saving…" : "Save"}
        </button>
      ) : (
        <p className="text-muted">Only an account admin can change this.</p>
      )}
    </form>
  );
}
